/* El cajón de compra · SOP-carrito.md
 *
 * Inserta sus piezas dentro del cajón NATIVO de Horizon sin editar un solo archivo suyo (D-015).
 * De arriba abajo (SOP §4.8): la cinta de confianza, el reloj y las fichas encima de las líneas;
 * en cada línea, la pastilla de la talla con su aviso de existencias; y en el pie, el renglón
 * verde del ahorro, el campo de código y los medios de pago. La tarjeta del par que sigue la
 * pone `aud-carrito-sug.js`, que escucha los dos avisos que este archivo despacha.
 *
 * DOS FUENTES, Y NO SE MEZCLAN (PR-3):
 *   lo que ya se ahorró  → la resta del propio Shopify. No se teclea, no puede mentir.
 *   lo que se ahorraría  → los escalones, declarados en el editor.
 *
 * SE ACTUALIZA EN SU SITIO, NO SE RECONSTRUYE (lección L-8, 2026-09-29). Antes cada repintado
 * borraba las piezas y las volvía a crear. Eso tenía dos consecuencias que se veían: el navegador
 * perdía la altura del documento y DABA UN SALTO DE SCROLL, y el lienzo del confeti desaparecía a
 * media explosión. Ahora cada pieza se crea UNA vez y después solo se le cambian valores, que es
 * además la única forma de que una transición de CSS llegue a correr.
 *
 * Y SE RECOLOCA EN EL MISMO INSTANTE EN QUE HORIZON LA BORRA (SOP §4.8, 2026-09-28). El morph de
 * Horizon es posicional y quita nuestros nodos en cada cambio del carrito. Volver a ponerlos
 * después de releer `/cart.js` dejaba 200 ms con el cajón sin reloj ni medidor: un tirón. Las
 * piezas se guardan por referencia y el observador las devuelve a su sitio en el mismo microtask,
 * antes de que el navegador pinte. Los VALORES llegan con el carrito; la pieza no parpadea.
 *
 * CADA PIEZA LLEVA SU PROPIA ETIQUETA, NO UN `div` (lección L-19, 2026-09-28). Para el morph de
 * Horizon dos nodos son «el mismo» si comparten etiqueta y no tienen `id` distinto. Un `div`
 * nuestro en la posición de un `div` suyo se lo quedaba: le copiaba las clases y los hijos, y la
 * referencia que guardábamos pasaba a apuntar A UNA PIEZA NATIVA. Medido: la cinta desaparecía
 * tras el primer repintado y no volvía. Con `<aud-cj-cinta>` no hay nodo nativo con el que
 * confundirla: lo peor que puede hacer el morph es sacarla, y eso ya se sabe arreglar.
 *
 * COMPUERTA ASIMÉTRICA (SOP §3.2 y §4.9): si lo que Shopify descuenta POR LÍNEA es menor que lo
 * prometido para los pares que hay, el medidor pasa a modo reducido: la cifra de la izquierda y
 * una frase, sin barra y sin promesa. Se mira el descuento de línea y no el total porque la
 * escalera cobra por par: medido el 2026-09-29, el automático viejo de −$30.000 es de PEDIDO, y
 * con dos pares pasaba por la escalera sin serlo.
 */

const CAJA = document.getElementById('aud-carrito-datos');
if (CAJA) {
  const D = JSON.parse(CAJA.textContent);
  const T = D.textos || {};
  const RLJ = D.reloj || {};
  const STK = D.stock || {};
  const CNT = D.cinta || {};
  const REV = D.revision || {};
  const SUAVE = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* LOS ESCALONES LOS DECLARA CADA BLOQUE, NO LA SECCIÓN (lección L-7 del SOP). En Horizon el
     padre ve `section.blocks` —los cuenta— pero NO puede leer sus `settings`: la sección medía
     5 bloques y sacaba la lista vacía. Así que cada escalón escribe sus dos cifras en su propio
     nodo y aquí se recogen. El módulo es `type="module"`, o sea diferido: cuando corre, los
     nodos ya están en el DOM. */
  function leerEscalones() {
    const nodos = document.querySelectorAll('[data-aud-escalon][data-n]');
    if (nodos.length) {
      return [...nodos]
        .map((e) => ({ n: parseInt(e.dataset.n, 10), ahorroPar: parseInt(e.dataset.ahorroPar, 10) }))
        .filter((e) => e.n > 1 && e.ahorroPar > 0);
    }
    return (D.escalones || []).slice();
  }

  const ESC = leerEscalones().sort((a, b) => a.n - b.n);

  /* ---------- dinero ---------- */
  function separar(centavos, mil, dec, conDecimales) {
    const neg = centavos < 0;
    const v = Math.abs(Math.round(centavos));
    const entero = String(Math.floor(v / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, mil);
    const resto = String(v % 100).padStart(2, '0');
    return (neg ? '-' : '') + entero + (conDecimales ? dec + resto : '');
  }

  function pesos(centavos) {
    const f = D.formato || '${{amount_no_decimals_with_comma_separator}}';
    return f.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, clave) => {
      switch (clave) {
        case 'amount': return separar(centavos, ',', '.', true);
        case 'amount_no_decimals': return separar(centavos, ',', '.', false);
        case 'amount_with_comma_separator': return separar(centavos, '.', ',', true);
        case 'amount_no_decimals_with_comma_separator': return separar(centavos, '.', ',', false);
        case 'amount_with_apostrophe_separator': return separar(centavos, "'", '.', true);
        case 'amount_with_space_separator': return separar(centavos, ' ', ',', true);
        case 'amount_no_decimals_with_space_separator': return separar(centavos, ' ', ',', false);
        default: return '';
      }
    });
  }

  function texto(plantilla, datos) {
    return String(plantilla || '').replace(/\{(\w+)\}/g, (m, c) => (c in datos ? datos[c] : m));
  }

  function limpiar(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---------- la escalera ---------- */
  const porPar = (n) => ESC.reduce((d, e) => (n >= e.n ? e.ahorroPar : d), 0);
  const esperado = (n) => porPar(n) * n;
  const siguiente = (n) => ESC.find((e) => e.n > n) || null;

  /* CUENTA EL PAR QUE CUESTA MÁS DEL MÍNIMO, no «el mínimo o más». La colección del descuento
     en Shopify es `VARIANT_PRICE GREATER_THAN 120000`; con `>=` una bota de $120.000 exactos
     contaba aquí y no allá. */
  const cuenta = (precio) => precio > D.minimo;

  /* QUIÉN ENTRA ES UNA COLECCIÓN, NO UN PRECIO (D-073). El descuento en Shopify apunta a la
     colección `ddt-escalera-volumen` —precio > $120.000 Y sin suela liviana—, y `/cart.js` no
     dice en qué colecciones está una línea: lo pide `sections/aud-oferta.liquid` con cada
     lectura del carrito, y aquí se guarda variante → asas. El precio queda de RESERVA para lo
     que el dato no alcanza: los bancos con carritos inventados, o una lectura que falló. */
  const OFERTA = D.oferta || '';
  let pertenencia = new Map();
  const entra = (it) => {
    const asas = OFERTA ? pertenencia.get(String(it.variant_id)) : null;
    return Array.isArray(asas) ? asas.includes(OFERTA) : cuenta(it.original_price);
  };

  function paresQueCuentan(carrito) {
    return (carrito.items || []).reduce((n, it) => n + (entra(it) ? it.quantity : 0), 0);
  }

  /* ---------- movimiento ----------
     Una sola curva, tres duraciones, y `prefers-reduced-motion` apaga todo. */
  const SUAVIZA = (k) => 1 - Math.pow(1 - k, 3);

  function cuadro(fn) {
    if (document.hidden || typeof requestAnimationFrame !== 'function') setTimeout(fn, 0);
    else requestAnimationFrame(fn);
  }

  /* La cifra no salta de $19.800 a $44.700: los recorre. Es la pieza que más se mira del cajón,
     y verla subir es exactamente el mensaje («esto crece si sumas»). */
  function rodar(nodo, hasta, pinta) {
    const desde = Number(nodo.dataset.valor || 0);
    nodo.dataset.valor = String(hasta);
    const giro = String(Number(nodo.dataset.giro || 0) + 1);
    nodo.dataset.giro = giro;
    if (!SUAVE || desde === hasta || document.hidden) {
      nodo.textContent = pinta(hasta);
      return;
    }
    const t0 = performance.now();
    const DUR = 620;
    const paso = (t) => {
      if (nodo.dataset.giro !== giro) return;
      const k = Math.min(1, (t - t0) / DUR);
      nodo.textContent = pinta(Math.round(desde + (hasta - desde) * SUAVIZA(k)));
      if (k < 1) requestAnimationFrame(paso);
    };
    requestAnimationFrame(paso);
  }

  function ponFrase(el, html) {
    if (el.dataset.txt === html) return;
    const primera = el.dataset.txt === undefined;
    el.dataset.txt = html;
    if (!SUAVE || primera) {
      el.innerHTML = html;
      return;
    }
    clearTimeout(Number(el.dataset.temp || 0));
    el.classList.add('aud-cj-med__frase--muda');
    el.dataset.temp = String(
      setTimeout(() => {
        el.innerHTML = html;
        el.classList.remove('aud-cj-med__frase--muda');
      }, 190)
    );
  }

  /* ---------- el medidor: las fichas ---------- (SOP §4.12 · D-064)
     LA ESCALERA, DICHA CON PALABRAS Y CON BOTAS. Tres fichas —las mismas de la ficha de
     producto—, cada una con cuántos pares, cuánto se ahorra y cuánto falta, y sus botas
     dibujadas: puestas las que ya están dentro, por poner las demás. */
  const VISTO =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  const BOTA =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M7 2.5h6.2v8.6l5.4 2.4c1.5.7 2.4 1.9 2.4 3.4V18H7z"/>' +
    '<rect x="6" y="19.3" width="15.5" height="2.4" rx="1.1"/></svg>';
  /* Más de doce botas no caben en una ficha de 105 px: ese escalón va sin dibujo. */
  const TOPE_BOTAS = 12;
  const POR_RENGLON = 5;
  const FICHAS = 3;

  function armarMedidor() {
    const el = document.createElement('aud-cj-medidor');
    el.className = 'aud-cj-med';
    /* Cómo llama la pastilla de la ficha que sigue: onda, latido, brillo o ninguno (D-067). */
    el.setAttribute('data-mov', D.movimiento || 'onda');
    el.setAttribute('data-aud-carrito', 'medidor');
    el.setAttribute('data-skip-node-update', '');
    el.innerHTML =
      '<p class="aud-cj-med__aviso" data-aviso hidden></p>' +
      '<div class="aud-cj-med__titular">' +
        '<span class="aud-cj-med__ceja" data-ceja></span>' +
        '<p class="aud-cj-med__dice" data-dice></p>' +
      '</div>' +
      '<div class="aud-cj-med__fichas" data-fichas role="list"></div>' +
      '<div class="aud-cj-med__pie">' +
        '<p class="aud-cj-med__frase" data-frase></p>' +
        '<p class="aud-cj-med__nota" data-nota hidden></p>' +
      '</div>';
    return el;
  }

  /* «Ref 2358 Gris», que es como la llama el comprador. Sin el patrón, el título entero. */
  function nombreCorto(titulo) {
    const m = /ref\W*(\d.*)$/i.exec(String(titulo || ''));
    return m ? 'Ref ' + m[1].trim() : String(titulo || '').trim();
  }

  /* «1 par», «2 pares». El número nunca va solo (SOP §4.12). */
  function pares(k) {
    return k + ' ' + (k === 1 ? T.unidadUno || '' : T.unidadVarios || '');
  }

  /* LA VENTANA DE TRES: el último premio ganado y los que siguen. Al alcanzar uno, el recién
     ganado pasa a ser el primero y por la derecha entra el siguiente. */
  function ventana(n) {
    const cuantas = Math.min(FICHAS, ESC.length);
    const sig = ESC.findIndex((e) => e.n > n);
    const desde = sig === -1
      ? ESC.length - cuantas
      : Math.max(0, Math.min(sig - 1, ESC.length - cuantas));
    return { desde: desde, sig: sig, cuantas: cuantas };
  }

  /* EL TITULAR. La frase es del editor; la cifra, de Shopify. `{ahorro}` se convierte en un
     hueco propio para que la cifra pueda rodar sin reescribir la frase entera. */
  function ponTitular(el, plantilla, n, ahorro) {
    const dice = el.querySelector('[data-dice]');
    const firma = String(plantilla) + '|' + n;
    if (dice.dataset.firma !== firma) {
      dice.innerHTML = limpiar(texto(plantilla, { pares: pares(n), n: n }))
        .replace('{ahorro}', '<b class="aud-cj-med__ahorro" data-ahorro></b>');
      dice.dataset.firma = firma;
    }
    const cifra = dice.querySelector('[data-ahorro]');
    if (cifra) rodar(cifra, ahorro, pesos);
  }

  function fichas(el, n) {
    const cont = el.querySelector('[data-fichas]');
    const v = ventana(n);
    const lista = ESC.slice(v.desde, v.desde + v.cuantas);
    const firma = lista.map((e) => e.n).join('-');

    if (cont.dataset.firma !== firma) {
      /* Cuánto corrió la ventana, para que las fichas entren por donde toca. Si saltó más de
         un puesto —de 1 par a 10, por ejemplo— no se anima: no hay recorrido que contar. */
      const antes = cont.dataset.desde === undefined ? null : Number(cont.dataset.desde);
      const paso = antes === null ? 0 : v.desde - antes;
      /* TODAS LAS FICHAS RESERVAN LOS MISMOS RENGLONES DE BOTAS. La de 10 pares dibuja dos
         renglones y sus vecinas uno: sin reservar el hueco, «ahorras», la cifra y la pastilla
         quedaban 16 px más abajo en esa ficha que en las otras (medido el 2026-09-29). La que
         no dibuja botas —un escalón de más de doce— guarda el sitio igual. */
      const dibuja = (e) => D.botas !== false && e.n <= TOPE_BOTAS;
      const renglones = Math.max(0, ...lista.map((e) => (dibuja(e) ? Math.ceil(e.n / POR_RENGLON) : 0)));
      cont.style.setProperty('--aud-cj-renglones', String(renglones || 1));
      cont.innerHTML = lista
        .map((e) =>
          '<div class="aud-cj-ficha" role="listitem" data-escalon="' + e.n + '">' +
            '<span class="aud-cj-ficha__pares">' + limpiar(pares(e.n)) + '</span>' +
            (dibuja(e)
              ? '<span class="aud-cj-ficha__botas" role="img" data-botas>' +
                new Array(e.n + 1).join('<span class="aud-cj-bota">' + BOTA + '</span>') + '</span>'
              : renglones
                ? '<span class="aud-cj-ficha__botas" aria-hidden="true"></span>'
                : '') +
            '<span class="aud-cj-ficha__dice">' + limpiar(T.fichaDice || '') + '</span>' +
            '<span class="aud-cj-ficha__premio">' + pesos(esperado(e.n)) + '</span>' +
            '<span class="aud-cj-ficha__estado" data-estado></span>' +
          '</div>'
        )
        .join('');
      cont.style.setProperty('--aud-cj-fichas', String(v.cuantas));
      cont.classList.remove('aud-cj-med__fichas--corre');
      if (SUAVE && Math.abs(paso) === 1) {
        cont.style.setProperty('--aud-cj-corre', paso > 0 ? 'calc(100% + 8px)' : 'calc(-100% - 8px)');
        void cont.offsetWidth;
        cont.classList.add('aud-cj-med__fichas--corre');
      }
      cont.dataset.firma = firma;
      cont.dataset.desde = String(v.desde);
      cont.dataset.pares = '';
    }

    const venia = cont.dataset.pares === '' ? null : Number(cont.dataset.pares);
    /* LA QUE YA SE TIENE (D-067): el último escalón ganado. Lleva el contorno, en verde. */
    const vale = ESC.reduce((suyo, e) => (n >= e.n ? e : suyo), null);
    [...cont.children].forEach((ficha, i) => {
      const e = lista[i];
      const hecha = n >= e.n;
      const sigue = v.desde + i === v.sig;
      const falta = e.n - n;
      const eraHecha = ficha.classList.contains('aud-cj-ficha--hecha');
      ficha.classList.toggle('aud-cj-ficha--hecha', hecha);
      ficha.classList.toggle('aud-cj-ficha--suya', Boolean(vale) && vale.n === e.n);
      ficha.classList.toggle('aud-cj-ficha--sigue', sigue);
      /* Rebota la que se acaba de ganar, y solo esa. */
      ficha.classList.toggle('aud-cj-ficha--recien', SUAVE && hecha && !eraHecha && venia !== null && venia < e.n);

      const estado = ficha.querySelector('[data-estado]');
      const dice = hecha
        ? VISTO + '<span>' + limpiar(T.estadoHecho || '') + '</span>'
        : limpiar(texto(falta === 1 ? T.estadoFaltaUno : T.estadoFaltan, { n: falta, pares: pares(falta) }));
      if (estado.dataset.html !== dice) {
        estado.innerHTML = dice;
        estado.dataset.html = dice;
      }

      const botas = ficha.querySelector('[data-botas]');
      if (botas) {
        const puestas = Math.min(n, e.n);
        botas.setAttribute('aria-label', texto(T.botasLectura, { n: puestas, max: e.n }));
        [...botas.children].forEach((b, j) => {
          const puesta = j < puestas;
          const era = b.classList.contains('aud-cj-bota--puesta');
          b.classList.toggle('aud-cj-bota--puesta', puesta);
          b.classList.toggle('aud-cj-bota--recien', SUAVE && puesta && !era && venia !== null);
        });
      }
    });
    cont.dataset.pares = String(n);
  }

  /* `real` es lo que Shopify descontó EN TOTAL y `cumple` dice si la escalera está cobrando lo
     que promete para estos pares. Devuelve `false` si no hay nada cierto que pintar. */
  function refrescarMedidor(el, n, real, cumple, simulado, fuera) {
    if (!cumple && !(real > 0)) return false;

    const sig = siguiente(n);
    el.classList.toggle('aud-cj-med--reducido', !cumple);

    const aviso = el.querySelector('[data-aviso]');
    const dice = simulado ? String(REV.aviso || '') : '';
    if (aviso.textContent !== dice) aviso.textContent = dice;
    aviso.hidden = !dice;

    const ceja = el.querySelector('[data-ceja]');
    if (ceja.textContent !== (T.titulo || '')) ceja.textContent = T.titulo || '';

    const frase = el.querySelector('[data-frase]');
    const nota = el.querySelector('[data-nota]');

    /* LOS PARES QUE NO ENTRAN A LA ESCALERA SE NOMBRAN (SOP §4.9). Con una Ref 2358 y otra
       bota dentro hay DOS pares a la vista y el medidor dice «Llevas 1 par»: sin esta nota,
       el comprador cree que el descuento no le entró. */
    const avisaFuera = cumple && fuera && fuera.length && T.fraseFuera
      ? texto(T.fraseFuera, { productos: fuera.join(', '), n: fuera.length })
      : '';
    if (nota.textContent !== avisaFuera) nota.textContent = avisaFuera;
    nota.hidden = !avisaFuera;

    if (!cumple) {
      /* Shopify descuenta algo, pero no es la escalera: se dice lo que hay y nada más. */
      ponTitular(el, T.llevasReducida, n, real);
      ponFrase(frase, texto(T.fraseReducida, { ahorro: pesos(real), n: n }));
      return true;
    }

    ponTitular(el, real > 0 ? T.llevas : T.llevasCero, n, real);
    fichas(el, n);

    if (sig) {
      /* LO QUE SE GANA SALE DE LO QUE YA SE AHORRÓ (SOP §4.9, fallo M-4): si la escalera cobra
         justo lo prometido es `esperado(sig.n)`; si hay algo más encima, sigue cuadrando. */
      const gana = esperado(sig.n) - esperado(n);
      const datos = {
        n: sig.n,
        ahorro: pesos(real + gana),
        delta: pesos(gana),
        porpar: pesos(sig.ahorroPar),
        faltan: sig.n - n,
      };
      ponFrase(
        frase,
        texto(n <= 1 ? T.frasePrimera : sig.n - n === 1 ? T.fraseSuma : T.fraseMayoreo, datos)
      );
    } else {
      ponFrase(frase, texto(T.fraseTope, { n: n, porpar: pesos(porPar(n)) }));
    }
    return true;
  }

  /* ---------- la cinta (SOP §4.8) ----------
     Los mensajes ya vienen pintados por Liquid, con sus iconos, dentro de una plantilla. Aquí
     solo se mueven. Cada uno lleva su propia `--x`: el que entra se coloca fuera SIN transición,
     se fuerza un cálculo de estilos, y entonces se le manda a su sitio. Con una pista que se
     desplaza entera, la vuelta del último al primero cruzaba todos los mensajes al revés. */
  let cintaIdx = 0;
  let cintaQuieta = 0;

  function armarCinta() {
    const molde = document.getElementById('aud-carrito-cinta');
    const el = molde && molde.content.firstElementChild && molde.content.firstElementChild.cloneNode(true);
    if (!el) return null;
    const items = el.querySelectorAll('[data-aud-item]');
    if (!items.length) return null;
    items.forEach((it, i) => {
      it.style.setProperty('--x', i === 0 ? '0%' : '100%');
      it.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    });
    if (items.length < 2) el.querySelectorAll('[data-aud-cinta]').forEach((b) => { b.hidden = true; });

    /* Con el dedo: un arrastre corto hacia un lado pasa de mensaje. */
    let x0 = null;
    el.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; cintaQuieta = 0; }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 28) moverCinta(el, dx < 0 ? 1 : -1);
    }, { passive: true });
    return el;
  }

  /* `paso` es +1 (siguiente) o −1 (anterior). El sentido del ajuste dice hacia dónde se va el
     que sale cuando se avanza: «derecha» lo saca por la derecha y trae el nuevo por la izquierda. */
  function moverCinta(el, paso) {
    const items = el.querySelectorAll('[data-aud-item]');
    if (items.length < 2) return;
    const sale = items[cintaIdx % items.length];
    cintaIdx = (cintaIdx + paso + items.length) % items.length;
    const entra = items[cintaIdx];
    const haciaDerecha = (CNT.sentido === 'izquierda' ? -1 : 1) * paso > 0;

    entra.style.transition = 'none';
    entra.style.setProperty('--x', haciaDerecha ? '-100%' : '100%');
    void entra.offsetWidth;
    entra.style.transition = '';
    entra.style.setProperty('--x', '0%');
    sale.style.setProperty('--x', haciaDerecha ? '100%' : '-100%');

    items.forEach((it) => it.setAttribute('aria-hidden', it === entra ? 'false' : 'true'));
    cintaQuieta = 0;
  }

  /* Un tic por segundo, el mismo del reloj. No avanza con el cajón cerrado, con la pestaña
     oculta, con el puntero o el foco encima, ni si el teléfono pide menos movimiento. */
  function latidoCinta() {
    const el = P.cinta;
    if (!el || !el.isConnected || !SUAVE || document.hidden) return;
    const dlg = el.closest('dialog');
    if (!dlg || !dlg.open) { cintaQuieta = 0; return; }
    if (el.matches(':hover') || el.matches(':focus-within')) { cintaQuieta = 0; return; }
    cintaQuieta += 1;
    if (cintaQuieta >= (CNT.segundos || 5)) moverCinta(el, 1);
  }

  /* ---------- el reloj (SOP §4.4) ----------
     `despacho` cuenta hacia la hora de corte y es cierto. `reserva` cuenta los minutos que el
     cliente haya decidido y solo se pinta si marcó la casilla que asume que Shopify NO aparta
     existencias. El plazo se guarda en el navegador: recargar la página no lo reinicia, que es
     justo lo que convierte un contador en feria. */
  const CLAVE = 'aud-carrito-reserva';

  function leerPlazo() {
    try { return Number(localStorage.getItem(CLAVE)) || 0; } catch (e) { return 0; }
  }

  function guardarPlazo(v) {
    try {
      if (v) localStorage.setItem(CLAVE, String(v));
      else localStorage.removeItem(CLAVE);
    } catch (e) { /* ventana privada: el reloj corre solo en esta vista */ }
  }

  function segundosCorte() {
    const partes = String(RLJ.horaCorte || '16:00').split(':');
    const corte = new Date();
    corte.setHours(Number(partes[0]) || 0, Number(partes[1]) || 0, 0, 0);
    return Math.floor((corte - Date.now()) / 1000);
  }

  function quedan() {
    if (RLJ.modo === 'despacho') return segundosCorte();
    let hasta = leerPlazo();
    if (!hasta) {
      hasta = Date.now() + (RLJ.minutos || 10) * 60000;
      guardarPlazo(hasta);
    }
    return Math.floor((hasta - Date.now()) / 1000);
  }

  function mmss(seg) {
    if (seg >= 3600) {
      return Math.floor(seg / 3600) + ':' + String(Math.floor((seg % 3600) / 60)).padStart(2, '0') + ' h';
    }
    /* Minutos a dos cifras: «09:52», no «9:52». Así la cuenta no cambia de ancho al bajar de
       10 minutos y la frase no da un salto en mitad del renglón. */
    return String(Math.floor(seg / 60)).padStart(2, '0') + ':' + String(Math.max(0, seg) % 60).padStart(2, '0');
  }

  /* EL COLOR DEL HILO VIAJA CON EL TIEMPO: azul de marca → miel → rojo. Se interpola en OKLab,
     no en RGB, y no es un capricho: mezclando en RGB el camino de azul a miel pasa por un verde
     oliva turbio —rgb(92 101 83) medido— y en este cajón el VERDE YA SIGNIFICA AHORRO. En OKLab
     el paso intermedio es un gris neutro. Además el primer tramo va acelerado al final, así que
     el azul se sostiene mientras sobra tiempo y el cambio se concentra cuando empieza a faltar. */
  function aLin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function aByte(c) {
    c = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(c * 255)));
  }
  function aOklab(rgb) {
    const r = aLin(rgb[0]), g = aLin(rgb[1]), b = aLin(rgb[2]);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
            1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
            0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s];
  }
  function aRgb(lab) {
    const l = Math.pow(lab[0] + 0.3963377774 * lab[1] + 0.2158037573 * lab[2], 3);
    const m = Math.pow(lab[0] - 0.1055613458 * lab[1] - 0.0638541728 * lab[2], 3);
    const s = Math.pow(lab[0] - 0.0894841775 * lab[1] - 1.2914855480 * lab[2], 3);
    return "rgb(" + aByte(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s) + " " +
                    aByte(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s) + " " +
                    aByte(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s) + ")";
  }
  const AZUL = [0, 101, 138], MIEL = [184, 101, 27], ROJO = [192, 54, 44];
  function mezcla(a, b, k) {
    const A = aOklab(a), B = aOklab(b);
    return aRgb([A[0] + (B[0] - A[0]) * k, A[1] + (B[1] - A[1]) * k, A[2] + (B[2] - A[2]) * k]);
  }
  function colorUrgencia(f) {
    if (f > 0.45) {
      const k = 1 - (f - 0.45) / 0.55;
      return mezcla(AZUL, MIEL, k * k * k);
    }
    return mezcla(MIEL, ROJO, 1 - Math.max(0, f) / 0.45);
  }

  /* FECHA DE ENTREGA (D-072, SOP-entrega.md). En el sitio del reloj va la tarjeta «Llega gratis
     el jueves»: la arma `aud-entrega.js` con las reglas de la sección «Fecha de entrega», las
     mismas que lee la ficha. Si ese guion o esa sección no están, no se pinta nada, y se vuelve
     a intentar en el siguiente repintado. */
  function datosEntrega() {
    const E = window.AudEntrega;
    return E && E.leerDatos ? E.leerDatos() : null;
  }

  function armarEntrega() {
    const d = datosEntrega();
    if (!d) return null;
    const el = document.createElement('aud-cj-entrega');
    el.setAttribute('data-aud-carrito', 'reloj');
    el.setAttribute('data-skip-node-update', '');
    el._aud = window.AudEntrega.montar(el, { cajon: true, textos: d.textos, reglas: d.reglas, mov: d.mov });
    return el;
  }

  function armarReloj() {
    if (RLJ.modo === 'entrega') return armarEntrega();
    const el = document.createElement('aud-cj-reloj');
    el.className = 'aud-cj-reloj';
    el.setAttribute('data-aud-carrito', 'reloj');
    el.setAttribute('data-skip-node-update', '');
    el.innerHTML =
      '<p class="aud-cj-reloj__fila" data-frase></p>' +
      (RLJ.hilo === false ? '' : '<div class="aud-cj-reloj__hilo"><i data-aud-hilo></i></div>');
    return el;
  }

  function refrescarReloj(el) {
    if (el._aud) { if (!document.hidden) el._aud.pinta(Date.now()); return; }
    const resta = quedan();
    const total = RLJ.modo === 'despacho' ? 3600 : (RLJ.minutos || 10) * 60;
    const fin = resta <= 0;
    const plantilla = fin ? RLJ.fraseFin : RLJ.frase;
    el.classList.toggle('aud-cj-reloj--fin', fin);

    const fila = el.querySelector('[data-frase]');
    const cuenta = fin ? '' : '<span class="aud-cj-reloj__cuenta" data-aud-cuenta>' + mmss(resta) + '</span>';
    /* {reloj} dice DÓNDE va la cuenta dentro de la frase. Sin él se añade al final. Así
       «Te guardamos el carrito por {reloj}» se lee como una frase y no como un cartel. */
    const nueva = String(plantilla || '').indexOf('{reloj}') >= 0
      ? String(plantilla).replace('{reloj}', cuenta)
      : String(plantilla || '') + (cuenta ? ' ' + cuenta : '');

    /* Solo se escribe lo que cambió. En reposo —la cuenta ya terminó— no se toca nada: antes
       se reescribía la frase entera cada segundo, con la misma frase. */
    if (fila.dataset.plantilla !== String(plantilla)) {
      fila.innerHTML = nueva;
      fila.dataset.plantilla = String(plantilla);
    } else {
      const c = fila.querySelector('[data-aud-cuenta]');
      const dice = mmss(resta);
      if (c && c.textContent !== dice) c.textContent = dice;
    }

    const hilo = el.querySelector('[data-aud-hilo]');
    if (hilo) {
      const f = total ? Math.max(0, Math.min(1, resta / total)) : 0;
      hilo.style.width = (f * 100).toFixed(2) + '%';
      hilo.style.backgroundColor = colorUrgencia(f);
    }
  }

  /* El reloj se pone al día EN SU SITIO. Repintar el cajón entero cada segundo es exactamente
     lo que producía el salto de scroll y lo que cortaba el confeti (L-8). */
  function latido() {
    if (P.reloj && P.reloj.isConnected) refrescarReloj(P.reloj);
    latidoCinta();
  }

  /* ---------- el confeti (SOP §4.5) ----------
     Sin librería: `canvas-confetti` y `motion` son peso que un estallido no justifica. Va por
     tiempo real (delta entre cuadros), no por cuadro, así que se ve igual de fluido a 60 y a
     120 Hz y no se arrastra si el hilo se atasca. Y NO lleva `data-aud-carrito`: si lo llevara,
     el siguiente repintado lo borraría a media explosión, que es lo que pasaba. */
  const COLORES = ['#1B7A3E', '#00658A', '#B8651B', '#0092C6', '#FFFFFF'];

  function confeti(host, x0, y0) {
    if (!SUAVE) return;
    const caja = host.getBoundingClientRect();
    if (!caja.width || !caja.height) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const lienzo = document.createElement('canvas');
    lienzo.className = 'aud-cj-confeti';
    lienzo.setAttribute('data-skip-node-update', '');
    lienzo.setAttribute('aria-hidden', 'true');
    lienzo.width = Math.round(caja.width * dpr);
    lienzo.height = Math.round(caja.height * dpr);
    lienzo.style.width = caja.width + 'px';
    lienzo.style.height = caja.height + 'px';
    host.appendChild(lienzo);
    const ctx = lienzo.getContext('2d');
    ctx.scale(dpr, dpr);

    const trozos = [];
    function estallar(cuantos, fuerza, retraso) {
      for (let i = 0; i < cuantos; i++) {
        const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.9;
        const vel = fuerza * (0.55 + Math.random() * 0.75);
        trozos.push({
          x: x0, y: y0,
          vx: Math.cos(ang) * vel,
          vy: Math.sin(ang) * vel,
          w: 3.5 + Math.random() * 4,
          h: 5 + Math.random() * 6,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.24,
          fase: Math.random() * Math.PI * 2,
          giro: 0.09 + Math.random() * 0.08,
          redondo: Math.random() < 0.28,
          color: COLORES[(Math.random() * COLORES.length) | 0],
          nace: retraso,
        });
      }
    }
    /* Dos estallidos escalonados: el segundo, más corto, evita que la nube se lea como una
       sola explosión de plantilla y alarga la sensación sin alargar la espera. */
    estallar(54, 7.4, 0);
    estallar(26, 5.2, 130);

    const VIDA = 2100;
    const t0 = performance.now();
    let previo = t0;
    /* Red por reloj: en una pestaña de fondo `rAF` no corre, el bucle no llega nunca al final
       y el lienzo se quedaría ahí para siempre. Medido el 2026-09-29. */
    setTimeout(() => lienzo.remove(), VIDA + 400);

    const paso = (t) => {
      if (!lienzo.isConnected) return;
      const e = t - t0;
      if (e > VIDA) { lienzo.remove(); return; }
      const dt = Math.min(48, t - previo) / 16.6667;
      previo = t;

      ctx.clearRect(0, 0, caja.width, caja.height);
      const k = e / VIDA;
      ctx.globalAlpha = k < 0.55 ? 1 : 1 - Math.pow((k - 0.55) / 0.45, 2);

      for (const p of trozos) {
        if (e < p.nace) continue;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 0.155 * dt;
        p.vx *= Math.pow(0.988, dt);
        p.rot += p.vr * dt;
        p.fase += p.giro * dt;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        /* El achatamiento finge la vuelta sobre su propio eje sin pasar a 3D. */
        ctx.scale(1, Math.max(0.15, Math.abs(Math.cos(p.fase))));
        ctx.fillStyle = p.color;
        if (p.redondo) {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 1.7, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
      requestAnimationFrame(paso);
    };
    requestAnimationFrame(paso);
  }

  /* ---------- existencias por talla ----------
     El número solo existe en Liquid: `/products/<handle>.js` no trae `inventory_quantity` y
     `/cart.js` tampoco. Se pide a `sections/aud-stock.liquid` por la Section Rendering API, una
     vez por variante y con memoria, así que un carrito de tres líneas hace tres consultas.
     Lo que ya contestó se guarda aparte (`stockListo`) para poder repintarlo SIN esperar: es lo
     que deja recolocar el aviso en el mismo instante en que Horizon repinta la línea. */
  const memoriaStock = new Map();
  const stockListo = new Map();

  function stockDe(item) {
    const clave = String(item.variant_id || item.id);
    if (!memoriaStock.has(clave)) {
      memoriaStock.set(
        clave,
        fetch('/products/' + item.handle + '?variant=' + clave + '&section_id=aud-stock')
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const n = doc.querySelector('[data-aud-stock]');
            return n ? JSON.parse(n.textContent) : null;
          })
          .catch(() => null)
          .then((dato) => {
            stockListo.set(clave, dato);
            return dato;
          })
      );
    }
    return memoriaStock.get(clave);
  }

  function filasReales(raiz) {
    /* `thead` lleva una fila con la MISMA clase. Se filtra por tener paso de cantidad (L-5). */
    return [...raiz.querySelectorAll('.cart-items__table-row')].filter((f) =>
      f.querySelector('button[name="plus"]')
    );
  }

  function varianteDe(fila) {
    const paso = fila.querySelector('cart-quantity-selector-component[data-variant-id]');
    return paso ? String(paso.dataset.variantId) : '';
  }

  /* El aviso entra DENTRO del envoltorio de la talla, no suelto en la línea: suelto, la rejilla
     lo mandaba debajo del paso de cantidad (lección L-18). */
  function ponStock(fila, dato) {
    const sitio = fila.querySelector('.cart-items__variants-wrapper');
    const viejo = fila.querySelector('.aud-cj-stock');
    const q = dato ? parseInt(dato.quedan, 10) : NaN;
    /* Tres condiciones, y las tres tienen que ser ciertas para que el número signifique algo:
       inventario gestionado por Shopify, política de NO vender sin existencias, y una cifra
       dentro del umbral. Con `continue` se vende en negativo y el número sería mentira. */
    const vale =
      sitio && STK.mostrar && STK.texto &&
      dato && dato.gestionado === 'shopify' && dato.politica === 'deny' &&
      q > 0 && q <= (STK.umbral || 3);
    if (!vale) {
      if (viejo) viejo.remove();
      return;
    }
    const plantilla = q === 1 ? STK.textoUno || STK.texto : STK.texto;
    const frase = limpiar(texto(plantilla, { n: q, talla: dato.talla }));
    if (viejo && viejo.dataset.txt === frase && viejo.parentElement === sitio) return;
    if (viejo) viejo.remove();
    const p = document.createElement('aud-cj-stock');
    /* Entra animado solo la primera vez que se ve; al recolocarlo tras un repintado, no. */
    p.className = 'aud-cj-stock' + (fila.dataset.audStock === frase ? '' : ' aud-cj-stock--entra');
    p.setAttribute('data-aud-carrito', 'stock');
    p.setAttribute('data-skip-node-update', '');
    p.dataset.txt = frase;
    p.innerHTML = '<span class="aud-cj-stock__punto"></span><span>' + frase + '</span>';
    sitio.appendChild(p);
    fila.dataset.audStock = frase;
  }

  async function pintarStock(raiz, carrito) {
    if (!STK.mostrar || !STK.texto) return;
    const porVariante = new Map((carrito.items || []).map((it) => [String(it.variant_id), it]));
    for (const fila of filasReales(raiz)) {
      const item = porVariante.get(varianteDe(fila));
      if (!item) continue;
      const dato = await stockDe(item);
      if (fila.isConnected) ponStock(fila, dato);
    }
  }

  /* ---------- montaje ---------- */
  let ahorroVisto = null;
  /* El escalón que valía en la lectura anterior, en pares: 0 si ninguno, `null` si todavía no
     hubo lectura. El confeti sale cuando SUBE, no cuando sube el ahorro (D-068). */
  let escalonVisto = null;
  let observador = null;
  let pendiente = false;
  let carritoEnCache = null;

  /* LAS PIEZAS, POR REFERENCIA. Se crean una vez y viven aquí aunque Horizon las saque del
     documento: recolocarlas es devolver el MISMO nodo a su sitio, con su estado puesto. */
  const P = { cinta: null, reloj: null, med: null, verde: null, pagos: null };

  function cajon() {
    return document.querySelector('#cart-drawer');
  }

  function vacio(raiz) {
    return !!raiz.querySelector('.cart-drawer--empty, [data-cart-drawer-empty]');
  }

  function quitar(raiz) {
    raiz.querySelectorAll('[data-aud-carrito]').forEach((n) => n.remove());
  }

  function avisar(nombre, detalle) {
    document.dispatchEvent(new CustomEvent('aud:carrito:' + nombre, { detail: detalle }));
  }

  /* EL CAMPO DE CÓDIGO. Nativamente Horizon lo pone ENTRE el subtotal y el total. Arriba del
     subtotal es donde el comprador que ya trae un cupón lo busca, y donde no compite con la
     cifra final. Es un nodo nativo que se MUEVE, no se copia: conserva su acordeón y su JS. */
  function colocarCodigo(raiz) {
    const pos = D.codigoPos || 'arriba';
    if (pos === 'nativo') return;
    const acciones = raiz.querySelector('.cart-actions');
    if (!acciones) return;
    const totales = raiz.querySelector('.cart-totals');
    const resumen = raiz.querySelector('.cart-drawer__summary');
    if (pos === 'arriba' && totales) {
      const ancla = totales.querySelector('.cart-totals__original-container') || totales.firstElementChild;
      if (ancla && ancla !== acciones && acciones.nextElementSibling !== ancla) {
        totales.insertBefore(acciones, ancla);
      }
    } else if (pos === 'abajo' && resumen && acciones.parentElement !== resumen) {
      resumen.appendChild(acciones);
    }
  }

  /* LA PASTILLA DE LA TALLA. La palabra («Talla») es un ajuste y se escribe en un atributo; el
     CSS la pinta delante del número. Solo en la opción que de verdad es la talla: el nombre de
     la opción viaja en el `dt`, oculto a la vista. */
  function ponTallas(raiz) {
    const prefijo = String(D.tallaPrefijo || '').trim();
    raiz.querySelectorAll('.cart-items__variant').forEach((v) => {
      const dd = v.querySelector('dd');
      if (!dd) return;
      const esTalla = prefijo && /talla/i.test((v.querySelector('dt') || {}).textContent || '');
      if (esTalla) {
        if (dd.getAttribute('data-aud-talla') !== prefijo) dd.setAttribute('data-aud-talla', prefijo);
      } else if (dd.hasAttribute('data-aud-talla')) {
        dd.removeAttribute('data-aud-talla');
      }
    });
  }

  /* EL TOTAL DENTRO DEL BOTÓN DE PAGAR. La cifra se lee del total que Horizon acaba de pintar
     en el pie, no del carrito: así el botón y el renglón de encima dicen siempre lo mismo, y
     se puede poner en el mismo instante del repintado sin esperar a la red. El total nativo
     trae el código de la moneda al final («$249,800 COP»); el botón no lo repite. */
  function ponTotalBoton(raiz) {
    if (!D.totalEnBoton || !T.textoBoton) return;
    const rotulo = raiz.querySelector('.cart__checkout-button .button-text');
    const total = raiz.querySelector('.cart-totals__total-value');
    if (!rotulo || !total) return;
    const cifra = String(total.getAttribute('value') || total.textContent || '')
      .trim()
      .replace(/\s*[A-Z]{3}$/, '');
    if (!cifra) return;
    const dice = texto(T.textoBoton, { total: cifra });
    if (rotulo.textContent.trim() !== dice) rotulo.textContent = dice;
  }

  /* COLOCAR ES SÍNCRONO Y NO PIDE NADA A LA RED. Pone cada pieza que ya existe en su sitio y
     retoca las líneas con lo que ya se sabe. Corre dentro del observador —antes de que el
     navegador pinte— y otra vez al montar. Si no hay nada que mover, no toca el DOM. */
  function colocar(raiz) {
    const lista = raiz.querySelector('.cart-drawer__items');
    if (!lista || vacio(raiz)) return false;

    /* arriba de la lista, en orden: cinta · reloj · medidor */
    let previo = null;
    [P.cinta, P.reloj, P.med].forEach((el) => {
      if (!el) return;
      const enSitio = el.parentNode === lista &&
        (previo ? previo.nextElementSibling === el : lista.firstElementChild === el);
      if (!enSitio) {
        if (previo) previo.insertAdjacentElement('afterend', el);
        else lista.insertAdjacentElement('afterbegin', el);
      }
      previo = el;
    });

    /* el pie: el código, el renglón verde y los medios de pago */
    colocarCodigo(raiz);
    const totales = raiz.querySelector('.cart-totals');
    const bloqueTotal = totales && totales.querySelector('.cart-totals__container');
    if (P.verde && bloqueTotal && P.verde.nextElementSibling !== bloqueTotal) {
      totales.insertBefore(P.verde, bloqueTotal);
    }
    const resumen = raiz.querySelector('.cart-drawer__summary');
    if (P.pagos && resumen && resumen.lastElementChild !== P.pagos) resumen.appendChild(P.pagos);

    ponTotalBoton(raiz);

    /* las líneas: la pastilla de la talla y el aviso que ya se conoce */
    ponTallas(raiz);
    filasReales(raiz).forEach((fila) => {
      const clave = varianteDe(fila);
      if (stockListo.has(clave)) ponStock(fila, stockListo.get(clave));
    });

    avisar('coloca', { raiz: raiz, lista: lista });
    return true;
  }

  /* AL ABRIRSE, EL CAJÓN ARRANCA ABAJO DEL TODO. Medido el 2026-09-29 con dos pares dentro:
     `.cart-drawer__content` abre con `scrollTop` 457,6 de un máximo de 458, o sea al final. El
     reloj quedaba en top −370 y el medidor en −329: los dos FUERA DE VISTA por arriba, y lo
     primero que veía el comprador era el pie. Pasa porque el `<dialog>` modal lleva el foco a un
     control del final y el navegador lo arrastra a la vista.

     Se vuelve al principio solo en la TRANSICIÓN de cerrado a abierto —nunca en un repintado—,
     para no pelearse con el comprador cuando él mismo esté desplazando. Y tres veces, porque el
     salto del foco puede llegar después del primer cuadro. */
  let abiertoAntes = false;

  function alAbrir(raiz) {
    const dlg = raiz.querySelector('dialog');
    const abierto = !!(dlg && dlg.open);
    if (abierto && !abiertoAntes) {
      /* QUIÉN DESPLAZA DEPENDE DEL PIE (medido el 2026-09-28). Con el pie suelto desplaza
         `.cart-drawer__content`; con el pie FIJO, Horizon le quita el alto al contenido y la
         que desplaza es `.cart-drawer__items`. Mientras el pie pesó más de medio cajón nunca
         estuvo fijo, y por eso solo se subía el primero. Ahora se suben los dos. */
      const arriba = () => {
        if (!dlg.open) return;
        raiz.querySelectorAll('.cart-drawer__content, .cart-drawer__items').forEach((c) => {
          if (c.scrollTop) c.scrollTop = 0;
        });
      };
      arriba();
      setTimeout(arriba, 60);
      setTimeout(arriba, 280);
      cintaQuieta = 0;
    }
    abiertoAntes = abierto;
  }

  /* EL PIE, FIJO O SUELTO. La regla es de Horizon (`cart-drawer.js`): si el pie pesa más de la
     mitad del cajón, se despega. Pero Horizon mide al abrir y al cambiar el carrito, y nuestras
     piezas del pie llegan un instante después. Se repite SU cuenta, con SU umbral, cuando ya
     está todo puesto. No se inventa una regla: se vuelve a aplicar la suya. */
  function ajustarPie(raiz) {
    const dlg = raiz.querySelector('dialog');
    const resumen = raiz.querySelector('.cart-drawer__summary');
    if (!dlg || !dlg.open || !resumen) return;
    const alto = dlg.getBoundingClientRect().height;
    if (!alto) return;
    const fijo = resumen.getBoundingClientRect().height / alto > 0.5 ? 'false' : 'true';
    if (dlg.getAttribute('cart-summary-sticky') !== fijo) dlg.setAttribute('cart-summary-sticky', fijo);
  }

  function montar(carrito) {
    const raiz = cajon();
    if (!raiz || !carrito) return;

    const lista = raiz.querySelector('.cart-drawer__items');
    if (!lista || vacio(raiz) || !(carrito.item_count > 0)) {
      quitar(raiz);
      guardarPlazo(0);
      ahorroVisto = null;
      escalonVisto = null;
      avisar('vacia', { raiz: raiz });
      return;
    }

    alAbrir(raiz);

    const n = paresQueCuentan(carrito);
    const real = carrito.total_discount != null
      ? carrito.total_discount
      : carrito.original_total_price - carrito.total_price;
    /* Lo que Horizon NO pinta: los descuentos de línea. Los de pedido ya llevan su renglón. */
    const deLinea = (carrito.items || []).reduce((t, it) => t + (it.line_level_total_discount || 0), 0);

    /* LA VISTA DE DISEÑO (SOP §4.9). Solo llega encendida a un tema SIN publicar —lo decide la
       sección, con `theme.role`— y solo actúa mientras Shopify no descuente nada por línea: el
       día que la escalera cobre, manda el dato real aunque la casilla siga puesta. Afecta al
       medidor y a la tarjeta del par que sigue; el total y el botón de pagar no se tocan. */
    const fuera = [...new Set(
      (carrito.items || []).filter((it) => !entra(it)).map((it) => nombreCorto(it.product_title))
    )];
    const simulado = !!REV.activa && n >= 2 && deLinea === 0;
    const ahorro = simulado ? esperado(n) : real;
    const cumple = simulado || deLinea >= esperado(n);

    /* 1. la cinta */
    if (D.sellos && !P.cinta) P.cinta = armarCinta();
    if (!D.sellos && P.cinta) { P.cinta.remove(); P.cinta = null; }

    /* 2. el reloj */
    const quiereReloj = RLJ.modo === 'entrega'
      ? !!datosEntrega()
      : RLJ.modo && RLJ.modo !== 'ninguno' && (RLJ.frase || RLJ.fraseFin);
    if (quiereReloj && !P.reloj) P.reloj = armarReloj();
    if (!quiereReloj && P.reloj) { P.reloj.remove(); P.reloj = null; }

    /* 3. el medidor. Si la escalera no cobra y tampoco hay nada descontado, no hay nada
          cierto que decir: no se pinta. */
    const quiereMed = n > 0 && ESC.length && (cumple || ahorro > 0);
    if (quiereMed && !P.med) P.med = armarMedidor();
    if (!quiereMed && P.med) { P.med.remove(); P.med = null; }

    /* 4. el renglón verde del pie */
    if (deLinea > 0 && !P.verde) {
      P.verde = document.createElement('aud-cj-ahorro');
      P.verde.className = 'aud-cj-ahorro';
      P.verde.setAttribute('data-aud-carrito', 'ahorro');
      P.verde.setAttribute('data-skip-node-update', '');
      P.verde.innerHTML = '<span data-rotulo></span><span data-cifra></span>';
    }
    if (!(deLinea > 0) && P.verde) { P.verde.remove(); P.verde = null; }

    /* 5. los medios de pago */
    const moldePagos = document.getElementById('aud-carrito-pagos');
    if (D.pagos && moldePagos && !P.pagos) P.pagos = moldePagos.content.firstElementChild.cloneNode(true);

    /* cada pieza a su sitio, y luego sus valores */
    colocar(raiz);

    if (P.reloj) refrescarReloj(P.reloj);
    if (P.med) refrescarMedidor(P.med, n, ahorro, cumple, simulado, fuera);
    if (P.verde) {
      P.verde.querySelector('[data-rotulo]').textContent = texto(T.rotuloAhorro, { n: n });
      rodar(P.verde.querySelector('[data-cifra]'), deLinea, (v) => '−' + pesos(v));
    }

    /* 6. las existencias por talla, que llegan por red y por eso van al final */
    pintarStock(raiz, carrito);

    ajustarPie(raiz);

    /* 7. el par que sigue vive en otro archivo. Se le pasa la cuenta ya hecha: lo que gana
       SUMAR UN PAR —que es lo que hace su botón—, y si la compuerta del §3.2 mordió. */
    avisar('monta', {
      raiz: raiz,
      lista: lista,
      carrito: carrito,
      pares: n,
      minimo: D.minimo,
      cuenta: cuenta,
      oferta: OFERTA,
      entra: entra,
      reducido: !cumple,
      gana: esperado(n + 1) - esperado(n),
      pesos: pesos,
      texto: texto,
      limpiar: limpiar,
    });

    /* La fiesta la dispara la cifra de SHOPIFY, no la promesa: si el comprador suma un par y el
       descuento no se movió, no hubo logro y no hay confeti (SOP §4.5).
       Y SALE AL ALCANZAR UN ESCALÓN, NO CON CADA PAR (D-068). Que el ahorro suba no basta: de 5
       a 6 pares sube —6 × $22.900 es más que 5 × $22.900— y no se ganó nada nuevo. Salía con
       cada par de 6 a 9. Si la escalera no está cobrando, el escalón que vale es ninguno. */
    const escalon = cumple ? ESC.reduce((suyo, e) => (n >= e.n ? e.n : suyo), 0) : 0;
    const subio = escalonVisto !== null && escalon > escalonVisto;
    escalonVisto = escalon;
    if (D.confeti && subio && ahorroVisto !== null && ahorro > ahorroVisto && P.med) {
      const marco = raiz.querySelector('.cart-drawer__inner') || raiz;
      const c = marco.getBoundingClientRect();
      /* Sale de la ficha que se acaba de ganar. Si no está a la vista —el comprador desplazó
         la lista, o el escalón quedó fuera de la ventana—, del centro del medidor. */
      const ganada = [...P.med.querySelectorAll('.aud-cj-ficha--hecha')].pop();
      let m = (ganada || P.med).getBoundingClientRect();
      if (!m.width || m.bottom < c.top || m.top > c.bottom) m = P.med.getBoundingClientRect();
      confeti(marco, m.left - c.left + m.width / 2, m.top - c.top + m.height / 2);
    }
    ahorroVisto = ahorro;
  }

  /* MANDA LA ÚLTIMA LECTURA, NO LA ÚLTIMA EN LLEGAR (lección L-23, 2026-09-29). Dos cambios
     seguidos del carrito son dos peticiones, y nada garantiza que contesten en orden: si la
     vieja llega después, pintaba el carrito anterior encima del nuevo. Cada lectura lleva su
     turno, y la que ya no es la última se tira. */
  let turno = 0;

  /* La sección de dato contesta HTML con un JSON dentro, como `aud-stock`. Si falla, se
     devuelve nada y `entra` cae a la reserva del precio: el medidor nunca se queda sin regla. */
  async function leerPertenencia(rutaCarrito) {
    if (navigator.onLine === false) return null; /* sin red no se pide: la reserva decide */
    try {
      const html = await fetch(rutaCarrito + '?section_id=aud-oferta').then((r) => (r.ok ? r.text() : ''));
      const n = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aud-oferta]');
      return n ? new Map(Object.entries(JSON.parse(n.textContent))) : null;
    } catch (e) {
      return null;
    }
  }

  /* UNA LECTURA POR RÁFAGA (SOP-velocidad §5.3, 2026-10-03). Medido en el inicio: cinco
     `/cart.js` y dos `aud-oferta` al cargar, porque cada mutación ajena del cajón —y Horizon lo
     hidrata en varias— pedía el carrito otra vez, y `aud-apps.js` leía por su cuenta. Ahora:
       · una lectura en vuelo se comparte: quien pida mientras tanto recibe la misma promesa.
         Medido: la ráfaga de la hidratación cae entera dentro de la primera lectura, así que
         con esto solo el inicio pasa de 3 lecturas nuestras a 1 (las otras dos que quedan son
         de STOQ, que lee `/cart.js` en cada página);
       · si hay una en vuelo y llega un EVENTO de carrito (cartLinesUpdate), se anota y se lee
         una vez más al terminar: nunca se pinta un carrito viejo (L-3, L-23 siguen valiendo).
     NO hay caché por tiempo ni espera: se probó una caché de 400 ms y el banco del medidor
     destapó que una lectura ajena reciente (la de `aud-apps.js`) tapaba un cambio del carrito
     que llegó sin evento; y una espera de 150 ms tras la mutación dejaba el número rodando
     cuando el banco lo leía. Lo que vale es compartir lo que está en vuelo, nada más. */
  let enVuelo = null;
  let otraVez = false;
  let pendienteEvento = false;

  function leerDeVerdad() {
    const rutas = window.Theme?.routes || window.routes || {};
    const url = (rutas.cart_url || '/cart') + '.js';
    const p = Promise.all([
      fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      OFERTA ? leerPertenencia(rutas.cart_url || '/cart') : null,
    ]).then(([leido, mapa]) => {
      carritoEnCache = leido;
      if (mapa) pertenencia = mapa;
      return leido;
    }).finally(() => {
      if (enVuelo === p) enVuelo = null;
      /* llegó un evento mientras se leía: una lectura más y, con ella ya en caché, se vuelve a
         pintar sin pedir otra (`programar(false)` monta lo que hay en memoria) */
      if (otraVez) { otraVez = false; leer(true).then(() => programar(false), () => {}); }
    });
    enVuelo = p;
    return p;
  }

  /* `fuerza`: un evento dijo que el carrito cambió; si hay una lectura en vuelo, se pide una
     más al terminar. Sin `fuerza`, la que está en vuelo basta. Devuelve siempre una promesa. */
  function leer(fuerza) {
    if (enVuelo) { if (fuerza) otraVez = true; return enVuelo; }
    return leerDeVerdad();
  }

  /* Para los bancos y para los demás módulos (`aud-apps.js` lee el total de aquí en vez de
     pedir `/cart.js` por su cuenta): qué sabe el cajón de cada línea, y qué decide. */
  window.AudCarrito = { entra: entra, pertenencia: () => new Map(pertenencia), oferta: OFERTA, leer: leer };

  async function refrescar(releer) {
    const mio = ++turno;
    if (releer || !carritoEnCache) {
      try {
        await leer(releer === 'evento');
        if (mio !== turno) return;
      } catch (e) {
        return; /* sin carrito no se inventa nada */
      }
    }
    observador?.disconnect();
    try {
      montar(carritoEnCache);
    } finally {
      vigilar();
    }
  }

  /* `releer` puede ser `false` (pintar lo que hay en memoria), `true` (una mutación: vale la
     lectura en vuelo) o `'evento'` (el carrito cambió de verdad: lectura nueva, aunque haya
     una en vuelo). */
  function programar(releer) {
    if (pendiente) { if (releer === 'evento') pendienteEvento = true; return; }
    pendiente = true;
    pendienteEvento = releer === 'evento';
    cuadro(() => {
      pendiente = false;
      refrescar(pendienteEvento ? 'evento' : releer);
    });
  }

  /* EL OBSERVADOR NO SE OYE A SÍ MISMO (2026-09-28). Medido en la tienda: 586 lecturas de
     `/cart.js` en una sola página abierta. El reloj escribe su cuenta cada segundo, las cifras
     ruedan cuadro a cuadro, y cada una de esas escrituras —dentro del cajón vigilado— pedía el
     carrito otra vez. De paso, esa relectura cortaba el rodar de las cifras a medio camino.
     Un cambio es NUESTRO si ocurre dentro de una pieza nuestra, o si es una pieza nuestra
     entrando. Que Horizon SAQUE una pieza nuestra no es nuestro: es justo lo que hay que oír. */
  function esPieza(nodo) {
    return nodo.nodeType === 1 &&
      (nodo.hasAttribute('data-aud-carrito') || nodo.classList.contains('aud-cj-confeti'));
  }

  function esAjeno(registro) {
    const t = registro.target.nodeType === 1 ? registro.target : registro.target.parentElement;
    if (t && t.closest('[data-aud-carrito], .aud-cj-confeti')) return false;
    if (registro.type === 'childList' && !registro.removedNodes.length &&
        registro.addedNodes.length && [...registro.addedNodes].every(esPieza)) {
      return false;
    }
    return true;
  }

  /* EL OBSERVADOR HACE DOS COSAS, Y EN ESTE ORDEN.
     Primero recoloca, YA: el aviso llega en el mismo microtask en que Horizon terminó su
     repintado, así que las piezas vuelven a su sitio antes de que el navegador pinte.
     Después pide el carrito, porque con la caché un repintado volvía a pintar el medidor con
     el carrito ANTERIOR: se veía «1 par» con dos pares dentro (L-3).
     Mientras recoloca se desconecta, o se muerde la cola con sus propios cambios. */
  function alCambiar(registros) {
    if (registros && !registros.some(esAjeno)) return;
    const raiz = cajon();
    if (raiz && observador) {
      observador.disconnect();
      try {
        colocar(raiz);
      } finally {
        vigilar();
      }
    }
    programar(true);
  }

  function vigilar() {
    const raiz = cajon();
    if (!raiz) return;
    observador = observador || new MutationObserver(alCambiar);
    /* `open` del <dialog> es un ATRIBUTO: sin vigilarlo, abrir el cajón no disparaba nada y
       `alAbrir` no llegaba a correr. */
    observador.observe(raiz, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  /* `aud-carrito-sug.js` carga después y con prioridad baja: si llega cuando el carrito ya se
     montó, lo pide otra vez. Se contesta con lo que hay en memoria, sin ir a la red. */
  document.addEventListener('aud:carrito:pide', () => programar(false));

  /* Las flechas de la cinta. */
  document.addEventListener('click', (e) => {
    const flecha = e.target.closest('[data-aud-cinta]');
    if (!flecha || !P.cinta || !P.cinta.contains(flecha)) return;
    moverCinta(P.cinta, Number(flecha.dataset.audCinta) < 0 ? -1 : 1);
  });

  async function arrancar() {
    try {
      const SE = await import('@shopify/events');
      if (SE?.StandardEvents?.cartLinesUpdate) {
        document.addEventListener(SE.StandardEvents.cartLinesUpdate, (ev) => {
          const listo = () => programar('evento');
          ev.promise ? ev.promise.then(listo).catch(listo) : listo();
        });
      }
    } catch (e) {
      /* sin el módulo, el observador solo se basta */
    }
    programar(true);
    vigilar();
    if ((RLJ.modo && RLJ.modo !== 'ninguno') || D.sellos) setInterval(latido, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar, { once: true });
  } else {
    arrancar();
  }
}
